import React, { useCallback } from 'react';
import { siInstagram, siSpotify, siBeatport, siApplemusic, siYoutube, siSoundcloud, siMixcloud, siDeezer } from 'simple-icons';
import { track as trackEvent } from '../../utils/analytics';
import { isFirstPartyUrlBlocked } from '../../utils/no1001';

// Icon-only socials row at the right end of the artist page's "Supported by"
// line (label page: the "LABEL · country" byline), built from the
// server's `externalIds` list. Only four services are shown — Instagram,
// Spotify, Beatport, Apple Music — as real brand glyphs (simple-icons' official
// paths, pure data, no runtime). Everything else the server sends is ignored
// here on purpose; 1001tracklists in particular must never surface anywhere.

// Display order.
const SHOWN_SOURCES = ['instagram', 'spotify', 'beatport', 'apple'];

// Track pages: where to buy / stream. Mix pages: where the set is hosted.
export const TRACK_SOURCES = ['beatport', 'spotify', 'apple', 'youtube', 'soundcloud', 'deezer'];
export const MIX_SOURCES = ['youtube', 'soundcloud', 'mixcloud'];

const LABELS = {
    youtube: 'YouTube',
    soundcloud: 'SoundCloud',
    mixcloud: 'Mixcloud',
    deezer: 'Deezer',
    instagram: 'Instagram',
    spotify: 'Spotify',
    beatport: 'Beatport',
    apple: 'Apple Music',
};

const GLYPHS = {
    youtube: siYoutube.path,
    soundcloud: siSoundcloud.path,
    mixcloud: siMixcloud.path,
    deezer: siDeezer.path,
    instagram: siInstagram.path,
    spotify: siSpotify.path,
    beatport: siBeatport.path,
    apple: siApplemusic.path,
};

const humanize = (source) => String(source || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const labelForSource = (source) => LABELS[source] || humanize(source) || 'Link';

const isHttp = (url) => typeof url === 'string' && /^https?:\/\//i.test(url.trim());

// Never render a 1001tracklists link (any source naming it, any url on it).
const isBanned = (source, url) => /1001/.test(String(source || '')) || /1001tracklists/i.test(String(url || '')) || isFirstPartyUrlBlocked(url);

// Dedupe by source (highest confidence wins), drop entries with no usable
// url, keep only the four shown services, in display order.
export function orderedLinks(externalIds, allowed = SHOWN_SOURCES) {
    if (!Array.isArray(externalIds)) return [];
    const bySource = new Map();
    for (const e of externalIds) {
        if (!e || !e.source || !isHttp(e.url)) continue;
        const source = String(e.source).toLowerCase();
        if (isBanned(source, e.url) || !allowed.includes(source)) continue;
        const prev = bySource.get(source);
        if (!prev || (e.confidence ?? 0) > (prev.confidence ?? 0)) bySource.set(source, { ...e, source, url: e.url.trim() });
    }
    return [...bySource.values()].sort((a, b) => allowed.indexOf(a.source) - allowed.indexOf(b.source));
}

// Mixes carry player links as `links: [{provider, url|src}]` (+ youtube ids),
// not externalIds — adapt them to the {source, url} shape the row expects.
export function mixLinkRows(mix) {
    const rows = [];
    for (const l of Array.isArray(mix?.links) ? mix.links : []) {
        if (!l || l.hidden) continue;
        rows.push({ source: String(l.provider || '').toLowerCase(), url: l.url || l.src });
    }
    const yt = mix?.youtubeId || (Array.isArray(mix?.youtubeIds) ? mix.youtubeIds[0] : null);
    if (yt && /^[\w-]{6,}$/.test(yt)) rows.push({ source: 'youtube', url: `https://www.youtube.com/watch?v=${yt}`, confidence: -1 });
    return rows;
}

// Same mechanism SubscriptionTab uses for store / checkout pages: the main
// process's `app:open-url` → shell.openExternal (https only). The web build
// has no bridge, so fall back to a new tab there.
export function openExternalUrl(url) {
    if (!isHttp(url)) return;
    if (typeof window !== 'undefined' && window.electron?.openUrl) {
        window.electron.openUrl(url);
        return;
    }
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
}

// `event`: 'artist_link_opened' | 'label_link_opened' | 'track_link_opened' |
// 'mix_link_opened' — literal names, all documented in docs/analytics-events.md.
export default function ArtistLinksRow({ externalIds, artistName, event = 'artist_link_opened', className = '', sources }) {
    const links = orderedLinks(externalIds, sources || SHOWN_SOURCES);
    const onOpen = useCallback((e, link) => {
        e.preventDefault();
        e.stopPropagation();
        const props = { source: link.source };
        if (event === 'label_link_opened') props.label_name = artistName || undefined;
        else if (event === 'track_link_opened') props.track_name = artistName || undefined;
        else if (event === 'mix_link_opened') props.mix_title = artistName || undefined;
        else props.artist_name = artistName || undefined;
        if (event === 'label_link_opened') trackEvent('label_link_opened', props);
        else if (event === 'track_link_opened') trackEvent('track_link_opened', props);
        else if (event === 'mix_link_opened') trackEvent('mix_link_opened', props);
        else trackEvent('artist_link_opened', props);
        openExternalUrl(link.url);
    }, [artistName, event]);
    if (links.length === 0) return null;
    return (
        <div className={`artist-socials-row${className ? ` ${className}` : ''}`} role="list" aria-label="Profiles">
            {links.map((link) => {
                const label = labelForSource(link.source);
                return (
                    <a
                        key={link.source}
                        role="listitem"
                        className="artist-social-btn"
                        href={link.url}
                        title={label}
                        aria-label={label}
                        onClick={(e) => onOpen(e, link)}
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                            <path d={GLYPHS[link.source]} />
                        </svg>
                    </a>
                );
            })}
        </div>
    );
}
